import { Heart, Calendar, Package, MessageSquare, FileText, LogOut, Menu, X, Star } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useCustomerAuth } from "./CustomerAuthProvider";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useState, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { UnifiedLanguageSwitcher } from "@/components/common/UnifiedLanguageSwitcher";
import { useLocalization } from "@/hooks/useLocalization";
import { useOrganizationBranding } from "@/hooks/useOrganizationBranding";
import { usePortalPWA } from "@/hooks/usePortalPWA";
import { Download } from 'lucide-react';
import { Skeleton } from "@/components/ui/skeleton";

export function CustomerPortalHeader() {
  const { customer, signOut } = useCustomerAuth();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { t } = useLocalization();
  const { name: orgName, logoUrl, brandColorHex, isLoading: brandingLoading } = useOrganizationBranding();
  const { canInstall, promptInstall } = usePortalPWA();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unpaidCount, setUnpaidCount] = useState(0);
  const [isInstalling, setIsInstalling] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  const getInitials = (firstName?: string, lastName?: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase() || 'C';
  };

  const getOrgInitials = (name: string) => {
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2);
  };

  const handleInstallApp = async () => {
    setIsInstalling(true);
    await promptInstall();
    setIsInstalling(false);
  };

  const fetchUnpaidCount = async () => {
    if (!customer?.email) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const { data, error } = await supabase.functions.invoke('customer-orders-list', {
        body: { 
          customer_email: customer.email,
          session_token: session?.access_token || 'customer_portal_session'
        }
      });

      if (!error && data && !data.error) {
        setUnpaidCount(data.summary?.totalUnpaid || 0);
      }
    } catch (error) {
      console.error('Error fetching unpaid count:', error);
    }
  };

  useEffect(() => {
    if (customer?.email) {
      fetchUnpaidCount();
      
      // Refresh count every 30 seconds
      const interval = setInterval(fetchUnpaidCount, 30000);
      return () => clearInterval(interval);
    }
  }, [customer]);

  // Get safe brand color with fallback
  const safeBrandColor = brandColorHex || '#EC4899';
  
  // Debug logging for brand color issues
  useEffect(() => {
    console.log('Brand color state:', { 
      brandColorHex, 
      safeBrandColor, 
      brandingLoading,
      orgName 
    });
  }, [brandColorHex, safeBrandColor, brandingLoading, orgName]);

  // Router paths are relative to BrowserRouter basename — no /portal prefix needed.
  const navigationItems = [
    { path: "/dashboard", icon: Heart, label: t('dashboard') },
    { path: "/appointments", icon: Calendar, label: t('appointments') },
    {
      path: "/orders",
      icon: Package,
      label: t('orders'),
      badge: unpaidCount > 0 ? unpaidCount : undefined
    },
    { path: "/help", icon: FileText, label: t('help') },
    {
      path: "/wishlist",
      icon: Heart,
      label: t('wishlist'),
      comingSoon: true
    },
    {
      path: "/loyalty",
      icon: Star,
      label: t('loyalty'),
      comingSoon: true
    },
  ];

  return (
    <header className="border-b bg-card sticky top-0 z-50">
      <div className="container mx-auto px-3 sm:px-4">
        <div className="flex h-14 sm:h-16 items-center justify-between">
          {/* Logo / Brand */}
          <Link
            to="/dashboard"
            className="flex items-center focus:outline-none focus:ring-2 focus:ring-primary rounded-lg p-1"
            aria-label="Go to dashboard"
          >
            {brandingLoading ? (
              <Skeleton className="h-20 w-20 sm:h-24 sm:w-24 rounded-full" />
            ) : logoUrl ? (
              <img 
                src={logoUrl} 
                alt={`${orgName} logo`}
                className="h-20 w-20 sm:h-24 sm:w-24 object-contain rounded-full"
              />
            ) : (
              <div 
                className="flex h-20 w-20 sm:h-24 sm:w-24 items-center justify-center rounded-full text-white text-3xl sm:text-4xl font-semibold transition-colors"
                style={{ backgroundColor: safeBrandColor }}
              >
                {getOrgInitials(orgName)}
              </div>
            )}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6" role="navigation" aria-label="Main navigation">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-2 text-sm transition-colors hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary rounded-lg p-2 touch-manipulation ${
                  isActive(item.path) ? 'text-primary font-medium' : 'text-muted-foreground'
                }`}
                aria-current={isActive(item.path) ? 'page' : undefined}
                >
                  <div className="relative">
                    <item.icon className="h-4 w-4" aria-hidden="true" />
                    {item.badge && (
                      <Badge 
                        variant="destructive" 
                        className="absolute -top-2 -right-2 h-4 w-4 rounded-full p-0 flex items-center justify-center text-xs"
                        aria-label={`${item.badge} unpaid invoices`}
                      >
                        {item.badge > 9 ? '9+' : item.badge}
                      </Badge>
                    )}
                  </div>
                  <span>{item.label}</span>
                  {item.comingSoon && (
                    <Badge variant="secondary" className="text-xs px-1 py-0 h-4">
                      {t('coming_soon')}
                    </Badge>
                  )}
              </Link>
            ))}
          </nav>

          <div className="flex items-center space-x-2">
            {/* Install App Button - Desktop */}
            {canInstall && !isMobile && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleInstallApp}
                disabled={isInstalling}
                className="hidden md:inline-flex"
              >
                <Download className="mr-2 h-4 w-4" />
                {isInstalling ? 'Installing...' : 'Install App'}
              </Button>
            )}
            
            {/* Language Toggle */}
            <UnifiedLanguageSwitcher variant="compact" />
            
            {/* Mobile Navigation */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="md:hidden h-11 w-11 p-0 touch-manipulation"
                  aria-label={t('menu')}
                  aria-expanded={mobileMenuOpen}
                  aria-controls="mobile-menu"
                >
                  <Menu className="h-5 w-5" aria-hidden="true" />
                  <span className="sr-only">{t('menu')}</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0" id="mobile-menu">
                <div className="flex flex-col h-full"
                     role="navigation" 
                     aria-label="Mobile navigation"
                >
                  <div className="p-4 border-b">
                    <div className="flex items-center space-x-2">
                      {brandingLoading ? (
                        <Skeleton className="h-10 w-10 rounded-full" />
                      ) : logoUrl ? (
                        <img 
                          src={logoUrl} 
                          alt={`${orgName} logo`}
                          className="h-10 w-10 object-contain rounded-full"
                        />
                      ) : (
                        <div 
                          className="flex h-10 w-10 items-center justify-center rounded-full text-white text-base font-semibold transition-colors"
                          style={{ backgroundColor: safeBrandColor }}
                        >
                          {getOrgInitials(orgName)}
                        </div>
                      )}
                      <span className="text-lg font-semibold">{brandingLoading ? 'Loading...' : orgName}</span>
                    </div>
                  </div>
                  
                  <nav className="flex-1 p-4">
                    <div className="space-y-2">
                      {navigationItems.map((item) => (
                        <Link
                          key={item.path}
                            to={item.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`flex items-center space-x-3 text-base transition-colors hover:text-primary p-3 rounded-lg hover:bg-accent focus:outline-none focus:ring-2 focus:ring-primary touch-manipulation ${
                              isActive(item.path) ? 'text-primary font-medium bg-accent' : 'text-foreground'
                            }`}
                            aria-current={isActive(item.path) ? 'page' : undefined}
                          >
                            <div className="relative">
                              <item.icon className="h-5 w-5" aria-hidden="true" />
                              {item.badge && (
                                <Badge 
                                  variant="destructive" 
                                  className="absolute -top-2 -right-2 h-4 w-4 rounded-full p-0 flex items-center justify-center text-xs"
                                  aria-label={`${item.badge} unpaid invoices`}
                                >
                                  {item.badge > 9 ? '9+' : item.badge}
                                </Badge>
                              )}
                            </div>
                            <span className="flex-1">{item.label}</span>
                            {item.comingSoon && (
                              <Badge variant="secondary" className="text-xs px-1 py-0 h-4">
                                {t('coming_soon')}
                              </Badge>
                            )}
                        </Link>
                      ))}
                    </div>
                  </nav>

                  <div className="p-4 border-t space-y-3">
                    <div className="flex items-center space-x-3">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback 
                          className="text-white font-semibold transition-colors"
                          style={{ backgroundColor: safeBrandColor }}
                        >
                          {getInitials(customer?.first_name, customer?.last_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {customer?.first_name} {customer?.last_name}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {customer?.email}
                        </p>
                      </div>
                    </div>
                    
                    {/* Install App Button - Mobile */}
                    {canInstall && (
                      <Button
                        variant="outline"
                        onClick={handleInstallApp}
                        disabled={isInstalling}
                        className="w-full justify-start touch-manipulation"
                      >
                        <Download className="mr-2 h-4 w-4" />
                        {isInstalling ? 'Installing...' : 'Install App'}
                      </Button>
                    )}
                    
                    <Button
                      variant="outline"
                      onClick={signOut}
                      className="w-full justify-start touch-manipulation"
                      aria-label={t('sign_out')}
                    >
                      <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                      {t('sign_out')}
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>

            {/* User Menu - Desktop - Mobile Optimized */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="relative h-11 w-11 rounded-full touch-manipulation min-w-[44px] focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={t('user_menu')}
                  aria-haspopup="menu"
                >
                  <Avatar className="h-9 w-9 sm:h-10 sm:w-10 ring-2 ring-background shadow-sm">
                    <AvatarFallback 
                      className="text-white font-semibold text-xs sm:text-sm transition-colors"
                      style={{ backgroundColor: safeBrandColor }}
                    >
                      {getInitials(customer?.first_name, customer?.last_name)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                className="w-64 z-[100] bg-popover border border-border shadow-lg backdrop-blur-sm" 
                align="end" 
                sideOffset={8}
                forceMount
              >
                <div className="flex flex-col space-y-1 p-2">
                  <p className="text-sm font-medium leading-none">
                    {customer?.first_name} {customer?.last_name}
                  </p>
                  <p className="text-xs leading-none text-muted-foreground break-all">
                    {customer?.email}
                  </p>
                  {customer?.wedding_date && (
                    <p className="text-xs leading-none text-muted-foreground">
                      Wedding: {new Date(customer.wedding_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={signOut} 
                  className="cursor-pointer touch-manipulation py-2 text-destructive focus:text-destructive focus:bg-destructive/10"
                  aria-label={t('sign_out')}
                >
                  <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                  <span>{t('sign_out')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}
